// dentLAB connected wrapper for the vendor dashboard
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCentralStore } from "../../../store/Central";
import { useRealtimeStore } from "@/lib/store/Realtime";
import { useConnectStore } from "../../../store/Connect";
import { useFormsStore } from "../../../store/Forms";
import { ActiveDelivery } from "@/components/src/deliveries-inbox/deliveries-inbox-widget.component";
import { UnpaidInvoice } from "@/components/src/unpaid-invoices/unpaid-invoices-widget.component";
import { VendorDashboard } from "@/components/src/vendor-dashboard/vendor-dashboard.component";
import { SYSTEM_STANDARD_FORM } from "@/dentconnect/src/pages/job-request/constants/system-standard-form";
import { supabase } from "@/lib/supabase";

export const VendorDashboardPageConnected: React.FC = () => {
	const { vendorId } = useParams<{ vendorId: string }>();
	const navigate = useNavigate();
	const [unpaidInvoices, setUnpaidInvoices] = useState<UnpaidInvoice[]>([]);
	const [activeDeliveries, setActiveDeliveries] = useState<ActiveDelivery[]>(
		[]
	);

	const profile = useCentralStore((s) => s.profile);
	const organization = useCentralStore((s) => s.organization);

	const latestChannels = useRealtimeStore((s) => s.latestChannels);
	const getNotificationNavigationPath = useRealtimeStore(
		(s) => s.getNotificationNavigationPath
	);
	const updateChannelReadState = useRealtimeStore(
		(s) => s.updateChannelReadState
	);
	const profilesForChat = useRealtimeStore((s) => s.profilesForChat);

	const brandsByOrgId = useConnectStore((s) => s.brandsByOrgId);

	const formsByOrgId = useFormsStore((s) => s.formsByOrgId);
	const fetchForms = useFormsStore((s) => s.fetchForms);

	useEffect(() => {
		if (vendorId) {
			fetchForms([vendorId]);
		}
	}, [vendorId]);

	useEffect(() => {
		if (organization?.id && vendorId) {
			fetchUnpaidInvoices();
			fetchActiveDeliveries();
		}
	}, [organization?.id, vendorId]);

	const fetchUnpaidInvoices = async () => {
		if (!organization?.id) return;
		const { data } = await (supabase.rpc as any)(
			"get_unpaid_shared_invoices",
			{ p_customer_org_id: organization.id }
		);
		if (data) {
			setUnpaidInvoices(data as UnpaidInvoice[]);
		}
	};

	const fetchActiveDeliveries = async () => {
		if (!organization?.id) return;
		const { data } = await (supabase.rpc as any)(
			"get_active_shared_deliveries",
			{ p_customer_org_id: organization.id }
		);
		if (data) {
			setActiveDeliveries(
				(data as any[]).map((d) => ({
					delivery_id: d.delivery_id,
					job_id: d.job_id,
					job_code: d.job_code,
					job_title: d.job_title,
					status: d.status,
					courier_type: d.courier_type,
					courier_company_name: d.courier_company_name,
					description: d.description,
					scheduled_departure_at: d.scheduled_departure_at,
					created_at: d.created_at,
					vendor_org_id: d.vendor_org_id,
					vendor_name: d.vendor_name,
				}))
			);
		}
	};

	const handleDeliveryClick = (delivery: ActiveDelivery) => {
		if (delivery.vendor_org_id && delivery.job_id) {
			navigate(
				`/vendor/${delivery.vendor_org_id}/job/${delivery.job_id}`
			);
		}
	};

	const handleInvoiceClick = (invoice: UnpaidInvoice) => {
		if (vendorId) {
			navigate(`/vendor/${vendorId}/invoices`);
		}
	};

	const handleFormClick = (formId: string) => {
		const isSystemForm = formId === SYSTEM_STANDARD_FORM.id;
		navigate(
			`/vendor/${vendorId}/job-request${
				formId && !isSystemForm ? `?formId=${formId}` : ""
			}`
		);
	};

	const getOrgName = (orgId: string): string | undefined => {
		return brandsByOrgId[orgId]?.name || undefined;
	};

	const vendorChannels = useMemo(() => {
		if (!vendorId) return [];
		return latestChannels.filter((ch) => ch.vendor_org_id === vendorId);
	}, [latestChannels, vendorId]);

	const vendorForms =
		vendorId && formsByOrgId[vendorId]
			? Object.values(formsByOrgId[vendorId])
			: [];

	const publishedForms = vendorForms
		.filter((form) => form.published_at)
		.map((form) => ({
			id: form.id,
			title: form.title,
			bgColor: form.bg_color,
		}));

	const formList =
		publishedForms.length > 0
			? publishedForms
			: [
					{
						id: SYSTEM_STANDARD_FORM.id,
						title: SYSTEM_STANDARD_FORM.title,
						bgColor: SYSTEM_STANDARD_FORM.bg_color,
					},
				];

	return (
		<VendorDashboard
			vendorId={vendorId}
			organizationId={organization?.id ?? ""}
			userId={profile?.id ?? ""}
			channels={vendorChannels}
			getNavigationPath={
				getNotificationNavigationPath ?? ((ch) => `/chats/${ch.id}`)
			}
			getOrgName={getOrgName}
			onMarkAsRead={updateChannelReadState}
			formList={formList}
			unpaidInvoices={unpaidInvoices}
			activeDeliveries={activeDeliveries}
			onDeliveryClick={handleDeliveryClick}
			onInvoiceClick={handleInvoiceClick}
			onFormClick={handleFormClick}
			profilesForChat={profilesForChat}
		/>
	);
};
